import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { CatalogItemCard } from '../components/CatalogItemCard';
import { CustomSelect } from '../components/CustomSelect';
import type { CatalogResponse, LeaderboardRow } from '../types';

function useCatalogFilters() {
  const [search, setSearch] = useState('');
  const [type, setType] = useState('All');
  const [rarity, setRarity] = useState('All');
  const [category, setCategory] = useState('All');
  const [page, setPage] = useState(1);
  return { search, setSearch, type, setType, rarity, setRarity, category, setCategory, page, setPage };
}

export function CatalogPage() {
  const filters = useCatalogFilters();
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [leaders, setLeaders] = useState<LeaderboardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      try {
        setLoading(true);
        setError(null);
        const query = new URLSearchParams({
          page: String(filters.page),
          limit: '24',
          search: filters.search,
          type: filters.type,
          rarity: filters.rarity,
          category: filters.category,
        });

        const [catalogData, leaderboardData] = await Promise.all([
          api.getCatalog(query),
          leaders.length === 0 ? api.getLeaderboard(8, 1) : Promise.resolve({ results: leaders, total: 0, page: 1, limit: 8 }),
        ]);

        if (!controller.signal.aborted) {
          setCatalog(catalogData);
          setLeaders(leaderboardData.results);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to load catalog');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, 180);

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [filters.page, filters.search, filters.type, filters.rarity, filters.category]);

  const totalPages = useMemo(() => {
    if (!catalog) return 1;
    return Math.max(1, Math.ceil(catalog.total / catalog.limit));
  }, [catalog]);

  return (
    <div className="catalog-page page-stack">
      <section className="hero-panel">
        <div>
          <p className="eyebrow">Flee The Facility</p>
          <h1>Powering no privacy</h1>
        </div>

        <div className="hero-stats">
          <StatCard label="Indexed Items" value={catalog?.stats.total ?? 0} />
          <StatCard label="Gems" value={catalog?.stats.gems ?? 0} />
          <StatCard label="Hammers" value={catalog?.stats.hammers ?? 0} />
          <StatCard label="Mythicals" value={catalog?.stats.mythicals ?? 0} accent />
        </div>
      </section>

      <section className="panel leaders-panel">
        <div className="panel__header">
          <div>
            <p className="eyebrow">Collectors</p>
            <h2>Top ranked players</h2>
          </div>
          <Link to="/rankings" className="text-link">Full board</Link>
        </div>
        <div className="leaders-grid">
          {leaders.map((player, index) => (
            <Link to={`/players/${player.roblox_id}`} key={player.roblox_id} className="leader-card">
              <span className="collector-rank">#{index + 1}</span>
              {player.headshot_url
                ? <img className="leader-avatar" src={player.headshot_url} alt={player.username} />
                : <div className="leader-avatar leader-avatar--placeholder" />}
              <div className="collector-copy">
                <strong>{player.display_name || player.username}</strong>
                <span>@{player.username}</span>
              </div>
              <div className="collector-metric">
                <strong>{player.total_items.toLocaleString()}</strong>
                <span>items</span>
              </div>
            </Link>
          ))}
          {leaders.length === 0 && !loading ? <div className="empty-state empty-state--compact">No ranking data yet.</div> : null}
        </div>
      </section>

      <div className="catalog-panel panel">
          <div className="panel__header panel__header--stack">
            <div>
              <p className="eyebrow">Catalog</p>
              <h2>All FTF items</h2>
            </div>
            <div className="filter-row">
              <input
                className="search-input"
                type="search"
                placeholder="Search by item name"
                value={filters.search}
                onChange={(event) => {
                  filters.setSearch(event.target.value);
                  filters.setPage(1);
                }}
              />
              <CustomSelect
                value={filters.type}
                options={['All', 'Gem', 'Hammer'].map((entry) => ({ value: entry, label: entry }))}
                onChange={(nextValue) => {
                  filters.setType(nextValue);
                  filters.setPage(1);
                }}
              />
              <CustomSelect
                value={filters.rarity}
                options={['All', ...(catalog?.rarities || [])].map((entry) => ({ value: entry, label: entry }))}
                onChange={(nextValue) => {
                  filters.setRarity(nextValue);
                  filters.setPage(1);
                }}
              />
              <CustomSelect
                value={filters.category}
                options={['All', ...(catalog?.categories || [])].map((entry) => ({ value: entry, label: entry }))}
                onChange={(nextValue) => {
                  filters.setCategory(nextValue);
                  filters.setPage(1);
                }}
              />
            </div>
          </div>

          {error ? <div className="empty-state">{error}</div> : null}
          {loading && !catalog ? <div className="empty-state">Loading item catalog...</div> : null}

          {catalog ? (
            <>
              <div className="catalog-summary">
                <span>{catalog.total.toLocaleString()} indexed results</span>
                <span>Page {catalog.page} of {totalPages}</span>
              </div>

              <div className="item-grid">
                {catalog.results.map((item) => <CatalogItemCard key={item.item_key} item={item} />)}
              </div>

              {catalog.results.length === 0 ? <div className="empty-state">No items matched those filters.</div> : null}

              <div className="pager">
                <button className="button" disabled={filters.page <= 1} onClick={() => filters.setPage((value) => Math.max(1, value - 1))}>Previous</button>
                <button className="button" disabled={filters.page >= totalPages} onClick={() => filters.setPage((value) => Math.min(totalPages, value + 1))}>Next</button>
              </div>
            </>
          ) : null}
      </div>
    </div>
  );
}

function StatCard({ label, value, accent = false }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className={`stat-card${accent ? ' stat-card--accent' : ''}`}>
      <span>{label}</span>
      <strong>{value.toLocaleString()}</strong>
    </div>
  );
}
